import { useState } from 'react';
import { Twitter, Linkedin, Link2, Check, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Social sharing buttons component
 * @param {Object} props
 * @param {string} props.url - URL to share
 * @param {string} props.title - Title/text to share
 * @param {string} props.description - Description for sharing (optional)
 * @param {string} props.variant - 'buttons' for individual buttons, 'dropdown' for menu
 * @param {string} props.className - Additional CSS classes
 */
export function SocialShareButtons({ 
  url, 
  title, 
  description = '',
  variant = 'buttons',
  className 
}) {
  const [copied, setCopied] = useState(false);

  // Get the current page URL if none provided
  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;

  // Encode for URL params
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedDescription = encodeURIComponent(description);

  // Share URLs
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShare = (platform) => {
    let shareLink = '';
    switch (platform) {
      case 'twitter':
        shareLink = twitterUrl;
        break;
      case 'linkedin':
        shareLink = linkedinUrl;
        break;
      default:
        return;
    }
    window.open(shareLink, '_blank', 'width=600,height=400');
  };

  // Dropdown variant
  if (variant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("gap-1.5", className)}
            data-testid="social-share-dropdown"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem 
            onClick={() => handleShare('twitter')}
            className="cursor-pointer gap-2"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4 text-[#1DA1F2]" />
            Share on Twitter
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleShare('linkedin')}
            className="cursor-pointer gap-2"
            data-testid="share-linkedin"
          >
            <Linkedin className="w-4 h-4 text-[#0A66C2]" />
            Share on LinkedIn
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={handleCopyLink}
            className="cursor-pointer gap-2"
            data-testid="share-copy-link"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                Link copied!
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Copy link
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Buttons variant (inline)
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('twitter')}
        className="gap-1.5 hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30"
        data-testid="share-twitter-btn"
      >
        <Twitter className="w-4 h-4" />
        <span className="hidden sm:inline">Twitter</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleShare('linkedin')}
        className="gap-1.5 hover:bg-[#0A66C2]/10 hover:text-[#0A66C2] hover:border-[#0A66C2]/30"
        data-testid="share-linkedin-btn"
      >
        <Linkedin className="w-4 h-4" />
        <span className="hidden sm:inline">LinkedIn</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyLink}
        className={cn(
          "gap-1.5 transition-colors",
          copied && "bg-green-50 text-green-600 border-green-200"
        )}
        data-testid="share-copy-btn"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            <span className="hidden sm:inline">Copied!</span>
          </>
        ) : (
          <>
            <Link2 className="w-4 h-4" />
            <span className="hidden sm:inline">Copy Link</span>
          </>
        )}
      </Button>
    </div>
  );
}
